import React from "react";

// constants and icons
import {
  IconUser,
  IconLogout2,
  IconMenu2,
  IconArrowNarrowLeft,
  IconSettings,
} from "@tabler/icons-react";

// state
import { get_profile, logout } from "../../../service/api/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../store";
import { NotificationPanel } from "../../../components/elements/form/NavigationPanel";
import { useMediaQuery } from "../../../hooks/useMediaQuery";

type INavigationBar = {
  onSidebar: () => void;
};

const NavigationBar: React.FC<INavigationBar> = ({ onSidebar }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const user = useAppSelector(({ auth }) => auth.user);
  const navbar = useAppSelector(({ navbar }) => navbar);
  const navigate = useNavigate();
  const [showPopover, setShowPopover] = React.useState(false);
  const popoverRef = React.useRef<HTMLDivElement>(null);

  const isMobile = useMediaQuery("(max-width: 768px)");

  const onLoad = React.useCallback(() => {
    get_profile(dispatch);
  }, [dispatch]);

  React.useEffect(() => {
    onLoad();
  }, [onLoad]);

  // Handle click outside to close popover
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setShowPopover(false);
      }
    };

    if (showPopover) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPopover]);

  const avatar = user?.profile?.avatar;

  const handleSettings = () => {
    navigate("/dashboard/settings");
    setShowPopover(false);
  };

  const handleLogout = () => {
    setShowPopover(false);
    logout(navigate);
  };

  return (
    <header className=" flex justify-between items-center bg-white px-6">
      <div className="text-[20px] font-bold flex justify-start items-center gap-1">
        <div className="flex items-center">
          {location.pathname !== "/dashboard" && (
            <IconArrowNarrowLeft
              size={30}
              color="#67C748"
              className="cursor-pointer hidden sm:block"
              onClick={() => navigate(-1)}
            />
          )}
          <IconMenu2
            size={25}
            className="sm:hidden text-primary-500 cursor-pointer"
            onClick={onSidebar}
          />
        </div>

        <div className="mx-2 text-base lg:text-xl text-primary-500">
          {navbar?.title
            ? isMobile && String(navbar.title).length > 13
              ? String(navbar.title).slice(0, 13) + " ..."
              : navbar.title
            : ""}
        </div>
      </div>

      <div className="text-right flex items-center justify-end">
        <NotificationPanel />

        {/* avatar with popover */}
        <div className="relative" ref={popoverRef}>
          <div
            className="flex flex-row items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => setShowPopover(!showPopover)}
          >
            {avatar ? (
              <div className="relative w-9 h-9 grid place-content-center bg-primary-50/30 rounded-[10px] text-primary-900 overflow-hidden">
                <img
                  src={avatar}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="relative w-9 h-9 grid place-content-center bg-black rounded-[10px] text-white">
                <IconUser className="h-5 w-5 " />
              </div>
            )}
            <p className="md:text-[15px] md:text-[#67C748] hidden md:block">
              {user?.profile?.name}
            </p>
          </div>

          {/* Popover */}
          <div
            className={`absolute right-0 mt-2 w-48 bg-white border border-gray-50/10 rounded-lg shadow-lg overflow-hidden transition-all duration-200 ease-out transform origin-top-right z-50 ${
              showPopover
                ? "opacity-100 scale-100 pointer-events-auto"
                : "opacity-0 scale-95 pointer-events-none"
            }`}
          >
            <div className="">
              {/* Settings Option */}
              <button
                onClick={handleSettings}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-100/20 transition-colors duration-150 text-gray-700 hover:text-primary-500"
              >
                <IconSettings size={18} />
                <span className="text-sm font-medium">Settings</span>
              </button>

              {/* Divider */}
              <div className="h-px bg-gray-200/20"></div>

              {/* Logout Option */}
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50/20 transition-colors duration-150 text-red-600 hover:text-red-700"
              >
                <IconLogout2 size={18} />
                <span className="text-sm font-medium">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default NavigationBar;
